import {
  Charge,
  DeliveryMethod,
  EnclosedItemCategory,
  InvoiceInput,
  InvoiceShipping,
  InvoiceShippingCharge,
  OrderStatus,
  OverrideFieldInput,
  OverrideFieldType,
  PackageType,
  PhoneType,
  ShippingChargeType,
  ShippingOption,
} from 'API';
import ModuleContainer from 'components/common/ModuleContainer';
import ModuleHeader from 'components/common/ModuleHeader';
import InvoiceDetail from 'components/invoicing/InvoiceDetail/InvoiceDetail';
import InvoiceHeader from 'components/invoicing/InvoiceHeader/InvoiceHeader';
import RightPanel from 'components/invoicing/RightPanel';
import { ROUTES } from 'components/navigation/Constants';
import { usePrevious } from 'hooks/use-previous';
import { sumBy } from 'lodash';
import { useAlertModal } from 'providers/AlertModalProvider';
import { useAuth } from 'providers/AuthProvider';
import { useInvoicingDetail } from 'providers/InvoicingDetailModuleProvider';
import { useManufacturingLocation } from 'providers/ManufacturingLocationProvider';
import { OverlayLoaderContext } from 'providers/OverlayLoaderProvider';
import { ToastContext } from 'providers/ToastProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getShippingOptions, invoice, InvoiceAccount, invoiceAccountLookup } from 'shared/api/invoice.api';
import { getOrderFull } from 'shared/api/order.api';
import { CancelActivity } from 'shared/constants/case-detail.constants';
import { INVALID_INVOICE_STATUSES } from 'shared/constants/invoice.constants';
import { PERMISSION_DENIED, PERMISSION_DENIED_TEXT } from 'shared/constants/role-based-access-control';
import { ErrorMessage, ShippingCarrier, ShippingService, ToastNotificationType } from 'shared/enums';
import { AnalyticsEventName } from 'shared/enums/analytics';
import { ACTIONS } from 'shared/enums/permission';
import {
  filterEnclosedItemsByShippingType,
  getDefaultShipping,
  getDoctorPickupCarrierItem,
  someCasesHaveMetalArticulator,
  validateMetalAlloyAttributes,
} from 'shared/helpers/invoice/invoice.helper';
import { getOutboundDiscountAmount, isLMSOrder } from 'shared/helpers/order-detail/order-detail.helper';
import { concatOrderNumber } from 'shared/helpers/order-entry/order-entry.helper';
import {
  checkPriceOrTaxBeingCalculated,
  roundDecimal,
  validateDiscountsAndSpecialOrderPartsForAllOrders,
} from 'shared/helpers/util.helper';
import { useLazyQueryFetcher } from 'shared/hooks/useLazyQueryFetcher';
import { useRoleBasedAccessControl } from 'shared/hooks/useRoleBasedAccessControl';
import { CreatedOrder, LocalOrderEnclosedItem } from 'shared/models';
import { InvoiceSummaryRouteState } from 'shared/models/invoicing-shipping';
import { AnalyticsService } from 'shared/services/analytics.service';
import {
  checkCaseInvalidInvoiceStatus,
  getCaseStatusRecord,
  getOrderCodAmount,
  getOutboundApplicableCase,
  getOutboundApplicableIndex,
  getPhoneToUse,
  getProductReturnRequirements,
  getShippingOptionsRequestBody,
  isAccountOnCod,
  isCreatedOrder,
} from 'shared/utils';
import { FetchShippingOptionsInput } from 'types/common';

/**
 * Renders a page presenting
 *  Case Information
 *  Shipment Selection
 *  Discount applied
 *  and an Invoice Summary
 */
const InvoicingPage: React.FC = () => {
  const { id: orderNumber } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const overlayLoader = useContext(OverlayLoaderContext);
  const toast = useContext(ToastContext);
  const {
    loading: orderLoading,
    fetcher: getOrderFetcher,
    initialized: invoiceInitialized,
  } = useLazyQueryFetcher(getOrderFull);
  const { loading: accountLoading, fetcher: invoiceAccountLookupFetcher } = useLazyQueryFetcher(invoiceAccountLookup);
  const [shippingLoading, setShippingLoading] = useState(true);
  const [bundleCaseChanged, setBundleCaseChanged] = useState(false);
  const { fetcher: getShippingOptionsFetcher } = useLazyQueryFetcher(getShippingOptions);
  const { fetcher: invoiceFetcher } = useLazyQueryFetcher(invoice);
  const [uTCConversionTimeZone, setUTCConversionTimeZone] = useState('');
  const [invoiceError, setInvoiceError] = useState<Error | null>(null);
  const {
    setAccount,
    setShipping,
    setCases,
    setInvoiceShippingInput,
    setOnSubmitAlertCount,
    invoiceShippingInput,
    invoiceCases,
    onSubmitAlertCount,
    account,
    getSelectedShippingData,
    setShippingOptions,
    spiltBundleCaseInfo,
    newCase,
    doNotShipOnSaturday,
    setProductReturnRequirements,
    codAmount,
    setCodAmount,
  } = useInvoicingDetail();
  const { getManufacturingLocation } = useManufacturingLocation();
  const { user } = useAuth();
  const currentUserName = user?.displayName || '';
  const alert = useAlertModal();
  const prevInvoiceCase = usePrevious(invoiceCases);
  const invoiceCasePermission = useRoleBasedAccessControl(ACTIONS.INVOICE_CASE);
  const prevOrderNumber = usePrevious(orderNumber);
  // Get the previous value of invoiceCases to detect bundle changes
  const prevInvoiceCases = usePrevious(invoiceCases);

  /**
   * In LMS1-9238, we prevented this hook from making duplicate shipping options calls on load, as well as when a call has already been made.
   * This hook was originally written as a fix for LMS1-8936, to refresh shipping options if a case is added/removed from the bundle.
   */
  useEffect(() => {
    if (!shippingLoading && invoiceCases.length > 0 && prevInvoiceCases?.length !== invoiceCases.length) {
      setBundleCaseChanged(true);
    }
  }, [prevInvoiceCases, invoiceCases, shippingLoading]);

  /**
   * Determines the currently processing order.
   *
   * - If selectedCaseId is provided, it finds the corresponding order from invoiceCases.
   * - Otherwise, it defaults to the first order in invoiceCases.
   */
  const processingOrder = useMemo(
    () => invoiceCases.find(invoiceCase => checkPriceOrTaxBeingCalculated(invoiceCase.metadata)),
    [invoiceCases]
  );
  const isOrderProcessing = useMemo(
    () => checkPriceOrTaxBeingCalculated(processingOrder?.metadata),
    [processingOrder?.metadata]
  );

  /**
   * !TODO: This is not a good way to handle this. We should be passing this data through the route state.
   * Actually, this cause a problem when the user directly navigates to this page.
   * We have to change this to a better way in future.
   */
  const ordersScanned = useMemo(() => location?.state?.ordersScanned || [], [location?.state?.ordersScanned]);

  /**
   * returns a list of alls RMA order items in all orders
   */
  const getAllInvoiceRmaOrderItems = useCallback(
    (orders: CreatedOrder[]) =>
      orders.flatMap(order => {
        return order.__typename === 'RmaOrder' ? order.orderItems : [];
      }),
    []
  );

  const {
    fetcher: getProductReturnRequirementsFetcher,
    data: productReturnRequirements,
    loading: isInvoiceSummaryLoading,
  } = useLazyQueryFetcher(getProductReturnRequirements);
  /**
   * Handles refresh of product return requirements.
   */
  const refreshProductReturnRequirements = useCallback(
    async (createdOrders: CreatedOrder[]) => {
      const allInvoiceRmaOrderItems = getAllInvoiceRmaOrderItems(createdOrders);
      const response = await getProductReturnRequirementsFetcher(allInvoiceRmaOrderItems);
      setProductReturnRequirements(response);
      return response;
    },
    [getAllInvoiceRmaOrderItems, getProductReturnRequirementsFetcher, setProductReturnRequirements]
  );

  /**
   * shows a warning alert dialog, if the user is not authorized to invoice an order
   */
  useEffect(() => {
    if (!invoiceCasePermission.allowCreate) {
      alert.show(PERMISSION_DENIED, PERMISSION_DENIED_TEXT);
      navigate(-1);
    }
  }, [alert, invoiceCasePermission, navigate]);

  /**
   * Fetches the ShippingOptions by using the fetcher function
   * @param invoiceCase - The main LMS case for which the shipping will be created
   * @param bundledOrders - The orders in this bundle.
   * @param account - The account which requests the invoice
   * @param subtotalAmount - Cost without shipping
   * @param codAmount - COD amount if any
   * @returns
   */
  const fetchShippingOptions = useCallback(
    async ({
      invoiceCase,
      bundledOrders,
      account: targetAccount,
      subtotalAmount,
      initialCodAmount,
    }: FetchShippingOptionsInput) => {
      /**
       * In LMS1-9229, we noticed that, when shipping options are re-loaded, the shipping loading boolean was still false.
       * This was allowing the user to submit the order, even when the options were re-calculating.
       */
      setShippingLoading(true);

      try {
        const requestBody = getShippingOptionsRequestBody(
          invoiceCase,
          bundledOrders,
          targetAccount,
          subtotalAmount,
          initialCodAmount
        );

        const result = await getShippingOptionsFetcher(requestBody);
        const location = getManufacturingLocation(invoiceCase.originFacilityId || 0);
        // generate doctor pickup carrier item for each package type
        const doctorPickupShippingOptions = Object.values(PackageType).map(packageType => {
          return getDoctorPickupCarrierItem(packageType, location?.locationName || 'Not Found');
        });
        // TODO: Update AppSync ShippingCharge.charges array to not be nullable. Per Carlos, value will always exist.
        // Don't show options that do not have a charge matching the service.
        const filterFn = (option: ShippingOption) => {
          return option.charges && option.charges.some(charge => charge.service === option.service);
        };
        const shippingOptions = result.shippingOptions.filter(filterFn);
        return [...shippingOptions, ...doctorPickupShippingOptions];
      } catch (err) {
        const error = err as Error;
        const errorMessage = error.message || 'Failed to fetch shipping options';
        throw new Error(errorMessage);
      } finally {
        setShippingLoading(false);
      }
    },
    [getManufacturingLocation, getShippingOptionsFetcher]
  );

  /**
   * fetched the order basing on orderNumber provided,
   * performs various checks and throws error accordingly if checks failed
   *
   * @param orderNumber - represents the order number of order which needs to be fetched
   * @returns {@link LmsOrder} if order is valid or null
   */
  const onFetchOrder = useCallback(
    async (orderNumber: string | undefined) => {
      if (!orderNumber) throw Error(ErrorMessage.INVALID_ORDER_ID);
      const invoiceCase = await getOrderFetcher({ orderNumber });

      if (!isCreatedOrder(invoiceCase)) {
        throw Error(`Type Order was not found`);
      }
      // If the case is canceled and discarded, throw an error.
      // Discarded cases cannot be invoiced, as per the business.
      const isCancelDiscard =
        invoiceCase &&
        invoiceCase.status === OrderStatus.Cancelled &&
        invoiceCase.statusReason === CancelActivity.Discard;
      if (isCancelDiscard) {
        throw Error(ErrorMessage.CANCELED_DISCARDED_CASE);
      }

      if (checkCaseInvalidInvoiceStatus(invoiceCase.status, invoiceCase.statusReason)) {
        throw Error(
          `You can't invoice this case because it is in ${getCaseStatusRecord(invoiceCase.status).displayName} status.`
        );
      }

      /**
       * Set cases and shipping before fetching shipping options since that request takes awhile and the UI needs to
       * react to some data on the case sooner.
       */
      invoiceCase.enclosedItems = filterEnclosedItemsByShippingType(
        invoiceCase.enclosedItems,
        EnclosedItemCategory.Outbound
      );
      return invoiceCase;
    },
    [getOrderFetcher]
  );

  /**
   * re-fetches the orders basing on order number provided
   *
   * - If caseId is provided, it pulls the latest data for that specific order.
   * - Otherwise, it defaults to processingOrder.orderNumber or orderNumber.
   */
  const onRefreshOrder = async (caseId?: string) => {
    /**
     * Determines the order number to fetch based on different scenarios:
     *
     * - processingOrder is set from the discount for bundle cases, where selectedCaseId is used to get the processingOrder when refreshing from other components.
     * - caseId is passed from InvoiceDiscountSection to fetch the discount-applied order data immediately after the add/remove discount succeeds.
     * - If neither caseId nor processingOrder is available, it falls back to orderNumber.
     */
    const defaultOrderNumber = processingOrder?.orderNumber || orderNumber;
    const orderNumberToFetch = caseId || defaultOrderNumber;
    try {
      const invoiceCase = await onFetchOrder(orderNumberToFetch);
      if (!invoiceCase) return;
      const newInvoiceCases = invoiceCases.map(caseItem => {
        if (caseItem.orderNumber === orderNumberToFetch) {
          return {
            ...invoiceCase,
            enclosedItems: caseItem.enclosedItems,
          };
        }
        return caseItem;
      });

      const filteredCases = newInvoiceCases.map(order => {
        return {
          ...order,
          enclosedItems: filterEnclosedItemsByShippingType(order.enclosedItems, EnclosedItemCategory.Outbound),
        };
      });
      setCases(filteredCases);

      // Refreshes product return requirements.
      await refreshProductReturnRequirements(filteredCases);
    } catch (err) {
      const error = err as Error;
      toast.notify(error.message, ToastNotificationType.Error);
    }
  };

  /**
   * Fetches shipping options based on the selected invoice case,
   * bundled orders, and initial COD amount, then updates the state.
   *
   * @param invoiceCase - The primary invoice case to calculate shipping for
   * @param bundledOrders - An array of all bundled invoice orders
   * @param initialCodAmount - Initial COD (Cash on Delivery) amount to include in the calculation
   */
  const fetchAndUpdateShippingOptions = useCallback(
    async (
      invoiceCase: CreatedOrder,
      bundledOrders: CreatedOrder[],
      initialCodAmount: number,
      account: InvoiceAccount
    ) => {
      const subtotalAmount = sumBy(bundledOrders, 'subtotalAmount');
      const shippingOptionsData = await fetchShippingOptions({
        invoiceCase,
        bundledOrders,
        account,
        subtotalAmount,
        initialCodAmount,
      });
      setShippingOptions(shippingOptionsData);
    },
    [setShippingOptions, fetchShippingOptions]
  );

  /**
   * Recalculates and sets the COD (Cash on Delivery) amount based on the outbound applicable case,
   * bundled orders, and account details.
   *
   * @param outboundApplicableCase - The order to determine if COD should be applied and retrieve its status
   * @param bundledOrders - All orders included in the current bundle, used to sum up COD amounts
   * @param account - The invoice account containing financial settings like base COD amount
   */
  const recalculateAndSetCodAmount = useCallback(
    (outboundApplicableCase: CreatedOrder | undefined, bundledOrders: CreatedOrder[], account: InvoiceAccount) => {
      const codAmountForAllOrders = sumBy(bundledOrders, invoiceCase => invoiceCase.shipping?.codAmount || 0);
      //if there is no outboundApplicableCase as per the AC, then set the codAmount as zero
      const initialCodAmount = outboundApplicableCase
        ? getOrderCodAmount(account.finance?.codAmount ?? 0, codAmountForAllOrders, outboundApplicableCase.status)
        : 0;
      setCodAmount(initialCodAmount);
      return initialCodAmount;
    },
    [setCodAmount]
  );
  /**
   * fetches invoice case and applies fetched values to state
   */
  useEffect(() => {
    const fetchInvoicingDetail = async () => {
      try {
        const invoiceCase = await onFetchOrder(orderNumber);

        if (location.state) {
          location.state.previousScannedInvoiceCase = invoiceCase;
        }

        // Loads all split bundle cases for the target order, if any exist. Please see LMS1-8823.
        const bundledOrderPromises: Promise<CreatedOrder>[] = [];

        invoiceCase.bundles?.splitBundle?.bundledOrderNumbers.forEach(bundledOrderNumber => {
          if (bundledOrderNumber.orderNumber !== invoiceCase.orderNumber) {
            bundledOrderPromises.push(onFetchOrder(bundledOrderNumber.orderNumber));
          }
        });

        const bundledOrders = [invoiceCase, ...(await Promise.all(bundledOrderPromises))];
        setCases(bundledOrders);

        // Refreshes product return requirements.
        const returnRequirements = await refreshProductReturnRequirements(bundledOrders);

        setShipping(invoiceCase.shipping);

        const targetAccount = await invoiceAccountLookupFetcher({
          billingAccountId: invoiceCase.billingAccountId || '',
        });
        if (targetAccount.__typename !== 'Account') throw Error(`Type Account was not found`);
        const outboundApplicableCase = getOutboundApplicableCase(bundledOrders, returnRequirements);

        // Sets the initial COD amount for this invoice case.
        const initialCodAmount = recalculateAndSetCodAmount(outboundApplicableCase, bundledOrders, targetAccount);
        setAccount(targetAccount);

        const manufacturingLocation = getManufacturingLocation(invoiceCase.originFacilityId || 10);
        if (!manufacturingLocation) throw Error(`Manufacturing Location was not found`);

        setUTCConversionTimeZone(manufacturingLocation.manufacturingFacilityTimeZoneId);

        const outboundApplicableCaseAsPerAC = outboundApplicableCase || bundledOrders[0];

        //if there is no outboundApplicableCase as per the AC, then pass the COD amount as zero
        const shippingOptionCodAmount = outboundApplicableCase ? initialCodAmount : 0;

        fetchAndUpdateShippingOptions(
          outboundApplicableCaseAsPerAC,
          bundledOrders,
          shippingOptionCodAmount,
          targetAccount
        );
      } catch (err) {
        const error = err as Error;
        setInvoiceError(error);
      }
    };

    // Only make request if the order number changes.
    if (orderNumber && orderNumber !== prevOrderNumber) {
      fetchInvoicingDetail();
    }
  }, [
    getManufacturingLocation,
    invoiceAccountLookupFetcher,
    location.state,
    onFetchOrder,
    orderNumber,
    prevOrderNumber,
    refreshProductReturnRequirements,
    setAccount,
    setCases,
    setShipping,
    setShippingOptions,
    setCodAmount,
    fetchAndUpdateShippingOptions,
    recalculateAndSetCodAmount,
  ]);

  /**
   * Effect to update shipping options whenever the number of invoice cases changes
   *
   * This ensures that the updated bundle data is used to fetch the updated shippingOptions
   * shipping options, especially when invoices are added or removed(when there is a change in the bundle).
   * Also verifies that isInvoiceSummaryLoading is false to confirm that
   * productReturnRequirements have been updated with the new invoiceCases array.
   */
  useEffect(() => {
    // Only trigger shipping options update if product return requirements exists with the updated data
    // and a case was added to or removed from the bundle (addedOrRemovedBundleCase is true).
    if (account && productReturnRequirements && bundleCaseChanged && !isInvoiceSummaryLoading) {
      const outboundApplicableCaseAsPerAC = getOutboundApplicableCase(invoiceCases, productReturnRequirements);
      const outboundApplicableCase = outboundApplicableCaseAsPerAC || invoiceCases[0];
      //recalculate the COD amount
      const newCODAmount = recalculateAndSetCodAmount(outboundApplicableCaseAsPerAC, invoiceCases, account);
      const shippingOptionCodAmount = outboundApplicableCaseAsPerAC ? newCODAmount : 0;
      fetchAndUpdateShippingOptions(outboundApplicableCase, invoiceCases, shippingOptionCodAmount, account);
      setBundleCaseChanged(false);
    }
  }, [
    productReturnRequirements,
    bundleCaseChanged,
    invoiceCases,
    codAmount,
    account,
    setCodAmount,
    fetchAndUpdateShippingOptions,
    recalculateAndSetCodAmount,
    isInvoiceSummaryLoading,
  ]);

  /**
   * triggered when invoicing is cancelled
   * triggers an analytics event, navigates back to previous page
   */
  const cancelHandler = () => {
    AnalyticsService.track(AnalyticsEventName.InvoiceCanceled);
    navigate(-1);
  };

  /**
   * Processes and return shipping overrides for this invoice, if any exist.
   * Returns the overwritten shipping fields for this invoice, if any exist.
   */
  const getShippingOverrides = (): OverrideFieldInput[] => {
    const overrides = [];
    const { carrierPreference, servicePreference, isSignatureRequiredPreference } = invoiceShippingInput;
    // If this account has an active preferred carrier, checks to see if any preferences have been overwritten.
    if (carrierPreference) {
      const isCarrierOverride = carrierPreference && invoiceShippingInput.carrier !== carrierPreference;
      if (isCarrierOverride) {
        overrides.push({
          field: 'carrier',
          type: OverrideFieldType.ShippingPreference,
          old: carrierPreference,
          new: invoiceShippingInput.carrier,
        });
      }
      const isSignatureRequiredOverride =
        isSignatureRequiredPreference !== null &&
        invoiceShippingInput.isSignatureRequired !== isSignatureRequiredPreference;
      if (isSignatureRequiredOverride) {
        overrides.push({
          field: 'signatureRequired',
          type: OverrideFieldType.ShippingPreference,
          old: isSignatureRequiredPreference ? 'yes' : 'no',
          new: invoiceShippingInput.isSignatureRequired ? 'yes' : 'no',
        });
      }

      const saturdayEntryFound = doNotShipOnSaturday();
      if (saturdayEntryFound && invoiceShippingInput.isSaturdayDelivery) {
        overrides.push({
          field: 'saturdayDelivery',
          type: OverrideFieldType.ShippingPreference,
          old: 'no',
          new: 'yes',
        });
      }

      const isServiceOverride =
        !isCarrierOverride && servicePreference && invoiceShippingInput.shippingService !== servicePreference;
      if (servicePreference && isServiceOverride) {
        overrides.push({
          field: 'shippingService',
          type: OverrideFieldType.ShippingPreference,
          old: servicePreference,
          new: invoiceShippingInput.shippingService,
        });
      }
    }
    return overrides;
  };

  /**
   * Validates the invoice data to make sure we are able to submit the invoice.
   */
  const isInvoiceValid = useCallback(() => {
    if (invoiceCases.some(order => !isLMSOrder(order.originatingSystem))) {
      // disallow users to submit an invoice, if the order is a legacy order
      // refer https://glidewell.atlassian.net/browse/LMS1-8469, for more details
      toast.notify(ErrorMessage.INVOICE_LEGACY_ORDER, ToastNotificationType.Error);
      return;
    }

    const { discountError, specialOrderPartsError } = validateDiscountsAndSpecialOrderPartsForAllOrders(invoiceCases);

    if (discountError) {
      toast.notify(discountError, ToastNotificationType.Error);
      return false;
    }

    if (spiltBundleCaseInfo.isBundle) {
      const missingBundledOrders = spiltBundleCaseInfo.bundledOrders.filter(order => {
        return !invoiceCases.find(cases => cases.orderNumber === order.orderNumber);
      });
      if (missingBundledOrders.length) {
        const singleOrPlural = missingBundledOrders.length > 1 ? 'cases' : 'case';
        const verb = missingBundledOrders.length > 1 ? 'are' : 'is';
        const concatOrderNumberString = concatOrderNumber(missingBundledOrders);
        toast.notify(
          `Missing bundled ${singleOrPlural}! "${concatOrderNumberString}" bundled ${singleOrPlural} ${verb} missing.`,
          ToastNotificationType.Error
        );
        return false;
      }
    }
    if (specialOrderPartsError) {
      alert.show('Missing Implant Part Value', specialOrderPartsError);
      return false;
    }

    // As per LMS1-7011, a cannot bundle error message should be shown for both outbound and split bundles.
    const isShippingBundle = !!invoiceShippingInput.trackingNumberBundledOrder || invoiceCases.length > 1;
    if (isShippingBundle && someCasesHaveMetalArticulator(invoiceCases)) {
      alert.show(
        'Cannot bundle these cases',
        'Cases with metal articulators cannot be bundled. Remove any cases with metal articulators and invoice them separately.'
      );

      return false;
    }

    /**
     * validates if patient details are valid for submission
     */
    const isPatientInfoValid = () => {
      return invoiceCases.every(invoiceCase => {
        return !!invoiceCase.patientFirstName || !!invoiceCase.patientLastName || !!invoiceCase.patientId;
      });
    };

    /**
     * validates if case is not valid for submission
     *
     * @see {@link INVALID_INVOICE_STATUSES} for all invalid cases
     */
    const isCaseStatusValid = () => {
      return invoiceCases.every(invoiceCase => !INVALID_INVOICE_STATUSES.includes(invoiceCase.status));
    };

    /**
     * validates if case has valid shipping options selected for submission
     */
    const isShippingValid = () => {
      return (
        !!invoiceShippingInput.carrier && !!invoiceShippingInput.packageType && !!invoiceShippingInput.shippingService
      );
    };

    let isEnclosedItemsValid = true;
    invoiceCases.forEach(invoiceCase => {
      const validateEnclosedItems = invoiceCase.enclosedItems.every(item => {
        return item.itemCode && item.quantity;
      });
      if (!validateEnclosedItems) {
        const enclosedItems = invoiceCase.enclosedItems.map(item => {
          return {
            ...item,
            errors: {
              itemCode: !item.itemCode ? 'Please select a enclosed item code' : '',
              quantity: !item.quantity ? 'Please enter a quantity' : '',
            },
          };
        });
        invoiceCase.enclosedItems = enclosedItems;
        isEnclosedItemsValid = false;
      }
    });

    if (!isEnclosedItemsValid) {
      setCases([...invoiceCases]);
    } else {
      invoiceCases.forEach(invoiceCase => {
        invoiceCase.enclosedItems.forEach((item: LocalOrderEnclosedItem) => {
          delete item.errors;
        });
      });
    }

    if (!isPatientInfoValid() || !isCaseStatusValid() || !isShippingValid() || !isEnclosedItemsValid) {
      alert.show('Missing Required Fields', 'Please make sure all required fields are completed before submitting.');
      return false;
    }

    const isValidMetalAlloyAttributes = invoiceCases.every(invoiceCase => {
      return validateMetalAlloyAttributes(invoiceCase.orderItems);
    });

    if (!isValidMetalAlloyAttributes) {
      toast.notify(ErrorMessage.MISSING_ALLOY_TYPE_OR_WEIGHT, ToastNotificationType.Error);
      return false;
    }

    return true;
  }, [
    alert,
    invoiceCases,
    invoiceShippingInput.carrier,
    invoiceShippingInput.packageType,
    invoiceShippingInput.shippingService,
    invoiceShippingInput.trackingNumberBundledOrder,
    setCases,
    spiltBundleCaseInfo.bundledOrders,
    spiltBundleCaseInfo.isBundle,
    toast,
  ]);

  /**
   * function which handles submission of case for invoicing
   *
   * @param printLabel - whether to print
   */
  const submitHandler = async (printLabel: boolean) => {
    setOnSubmitAlertCount(onSubmitAlertCount + 1);

    if (!isInvoiceValid()) return;

    const selectedShippingData = getSelectedShippingData();
    const selectedShippingOptionFromData = selectedShippingData.selectedShippingOption;
    const outboundShippingCharge = selectedShippingData.outboundShippingCharge;
    const codFee = selectedShippingData.codFee;

    if (!selectedShippingOptionFromData || !uTCConversionTimeZone || !orderNumber) {
      toast.notify('Selected Shipping Option not found.', ToastNotificationType.Error);
      setInvoiceShippingInput({
        carrier: '',
        packageType: null,
        shippingService: '',
      });
      return;
    }

    const isDoctorPickUp = selectedShippingOptionFromData.carrier === ShippingCarrier.DoctorPickup;

    if (!isDoctorPickUp && !outboundShippingCharge) {
      toast.notify('Selected Shipping Option not found.', ToastNotificationType.Error);
      setInvoiceShippingInput({
        carrier: '',
        packageType: null,
        shippingService: '',
      });
      return;
    }

    const buildCharge = (
      shippingCharge: Charge,
      cost: number,
      type: ShippingChargeType,
      serviceOverride?: string
    ): InvoiceShippingCharge => {
      let discountAmount = 0;
      // If the type is outbound, calculate the discount amount.
      if (type === ShippingChargeType.Outbound) {
        discountAmount = getOutboundDiscountAmount(cost, selectedShippingData.outboundDiscountPercentage);
      }
      // totalAmount represents the total cost of the shipping charge after the discount has been applied.
      const totalAmount = roundDecimal(cost - discountAmount);
      return {
        amount: cost,
        carrier: selectedShippingOptionFromData.carrier,
        discountAmount: discountAmount,
        id: shippingCharge.shippingChargeId,
        salesTaxAmount: 0,
        service: serviceOverride || selectedShippingOptionFromData.service,
        totalAmount: totalAmount,
        type: type,
        taxCode: shippingCharge.taxCode,
      };
    };

    const shippingCharges = [
      buildCharge(outboundShippingCharge, selectedShippingData.outboundShippingChargeCost, ShippingChargeType.Outbound),
    ];

    const codFeePrice = codFee?.currencies.find(currency => currency.currencyCode === account?.currencyCode)?.price;
    if (codFee && codFeePrice) {
      shippingCharges.push(buildCharge(codFee, codFeePrice, ShippingChargeType.Cod, ShippingService.COD_FEE));
    }

    if (selectedShippingData.signatureRequiredCharge) {
      shippingCharges.push(
        buildCharge(
          selectedShippingData.signatureRequiredCharge,
          selectedShippingData.signatureRequiredCost,
          ShippingChargeType.Outbound,
          selectedShippingData.signatureRequiredCharge.service
        )
      );
    }

    if (selectedShippingData.saturdayDeliveryCharge) {
      shippingCharges.push(
        buildCharge(
          selectedShippingData.saturdayDeliveryCharge,
          selectedShippingData.saturdayDeliveryCost,
          ShippingChargeType.Outbound,
          selectedShippingData.saturdayDeliveryCharge.service
        )
      );
    }

    const deliveryMethod = isDoctorPickUp ? DeliveryMethod.DoctorPickup : DeliveryMethod.Carrier;

    const invoiceShipping: InvoiceShipping = {
      packageType: selectedShippingOptionFromData.package,
      deliveryMethod,
      doctorPickupLocationId: isDoctorPickUp ? invoiceCases[0].originFacilityId : null,
    };

    /**
     *If there is a cod amount, adds it to the invoice shipping request object.
     */
    if (codAmount) {
      invoiceShipping.codAmount = codAmount;
    }

    if (isDoctorPickUp) {
      const { number: phoneToUseNumber, type: phoneToUseType } = getPhoneToUse(account?.phoneNumbers || []) || {};
      // The services team has requested that we not send them phone information if the phone number is blank. Please see LMS1-7140.
      if (phoneToUseNumber) {
        invoiceShipping.phone = {
          number: phoneToUseNumber,
          type: phoneToUseType ?? PhoneType.Home,
        };
      }
    }

    const trackingNumberBundledOrder = invoiceShippingInput.trackingNumberBundledOrder;

    /**
     * Only add data if we are not bundling with tracking number or
     * if the tracking number we are bundling with is not charge eligible.
     * For not charge eligible case, reuse the rateId, shipmentId, and shipping charges so
     * that we do not purchase a new label.
     */
    if (!trackingNumberBundledOrder || !trackingNumberBundledOrder.isChargeEligible) {
      const bundleCharges = trackingNumberBundledOrder?.shippingCharges.map(charge => ({
        ...charge,
        salesTaxAmount: charge.salesTaxAmount || 0,
        discountAmount: getOutboundDiscountAmount(charge.amount, selectedShippingData.outboundDiscountPercentage),
      }));
      invoiceShipping.rateId = trackingNumberBundledOrder?.rateId || selectedShippingOptionFromData.rateId;
      invoiceShipping.shipmentId = trackingNumberBundledOrder?.shipmentId || selectedShippingOptionFromData.shipmentId;
      invoiceShipping.shippingCharges = !isDoctorPickUp ? bundleCharges || shippingCharges : [];
    }

    const invoiceInput: InvoiceInput = {
      invoiceBy: currentUserName,
      orderInvoices: [],
      uTCConversionTimeZone,
    };

    // Added bundleOrderNumber if we are bundling with existing tracking number.
    if (trackingNumberBundledOrder) {
      invoiceInput.bundledOrderNumber = trackingNumberBundledOrder.orderNumber;
    }

    const shippingApplicableNextCaseIndex = getOutboundApplicableIndex(invoiceCases, productReturnRequirements || []);
    // If the account has a COD amount per additional case and shippingApplicableNextCaseIndex is > -1, use it. Otherwise, default to 0.
    const codAmountPerAdditionalCase =
      (shippingApplicableNextCaseIndex > -1 && account?.finance?.codAmountPerAdditionalCase) || 0;

    // Create invoice cases for input. Only add shipping to the charge eligible case (if it exists).
    const invoiceInputCases = invoiceCases.map(
      ({ orderNumber: invoiceCaseOrderNumber, coupons, enclosedItems }, index) => ({
        orderNumber: invoiceCaseOrderNumber,
        coupons,
        enclosedItems,
        shipping:
          index === shippingApplicableNextCaseIndex // Only add charges to the charge eligibile case.
            ? invoiceShipping
            : getDefaultShipping(deliveryMethod, invoiceShippingInput.packageType, codAmountPerAdditionalCase),
      })
    );

    /**
     * If there is no case found that can apply outbound shipping costs,
     * and we are not bundling with tracking number (don't discount case if we already have a case with charges):
     * attach full shipping to first case with no charge (discount equal to total for all charges).
     * Originally implemented to support RMA cases.
     */
    if (shippingApplicableNextCaseIndex < 0 && !trackingNumberBundledOrder) {
      invoiceShipping.shippingCharges = invoiceShipping.shippingCharges?.map(charge => ({
        ...charge,
        discountAmount: charge.amount,
      }));
      invoiceInputCases[0].shipping = invoiceShipping;
    }

    invoiceInput.orderInvoices = invoiceInputCases;

    // Processes and return shipping overrides for this invoice, if any exist.
    invoiceInput.overrides = getShippingOverrides();
    overlayLoader.show('Creating invoice');

    try {
      await invoiceFetcher(invoiceInput);

      const orderInvoices = invoiceInput.orderInvoices || [];
      const accountNumber = invoiceCases[0]?.billingAccountId || '';
      const ordersInBundle = orderInvoices.map(order => order.orderNumber).join(', ');
      orderInvoices.forEach(order => {
        AnalyticsService.track(AnalyticsEventName.CaseInvoiced, {
          ...invoiceInput,
          caseNumber: order.orderNumber ?? '',
          accountNumber,
          ordersInBundle,
        });
      });

      overlayLoader.hide();
    } catch {
      overlayLoader.hide();
      toast.notify('Failed to create invoice.', ToastNotificationType.Error);
      return; // Do not navigate if the invoice fails.
    }

    const navState: InvoiceSummaryRouteState = {
      orders: invoiceCases,
    };

    if (printLabel) {
      navState.printLabelData = {
        orderNumber,
        type: invoiceShipping.deliveryMethod || DeliveryMethod.Carrier,
        printInboundLabel: isAccountOnCod(account?.standing),
      };
    }

    const designImagingSpotOpposings = invoiceCases
      .map(invoiceCase =>
        invoiceCase.fileAttachments.find(
          attatchment => attatchment.uploadProperties.fileType === 'DesignImageSpotOpposing'
        )
      )
      .filter(Boolean);

    if (navState.printLabelData && designImagingSpotOpposings.length) {
      navState.printLabelData.opposingImageFilePaths = designImagingSpotOpposings.map(
        attachment => attachment?.fileUrl || ''
      );
    }

    if (spiltBundleCaseInfo.bundleId) {
      navState.bundleId = spiltBundleCaseInfo.bundleId;
    }

    navigate(
      {
        pathname: ROUTES.INVOICE_ORDER_LOOKUP,
      },
      {
        state: navState,
      }
    );
  };

  /**
   * get the title of the invoicing page based on the bundle case info.
   */
  const invoicingHeaderTitle = useMemo(() => {
    if (!spiltBundleCaseInfo.isBundle) return 'Invoicing';
    return `Invoicing Bundle ${spiltBundleCaseInfo.bundleId}`;
  }, [spiltBundleCaseInfo]);

  const isOrderLoading = (orderLoading && !invoiceInitialized) || accountLoading;
  /**
   * Ensure that shipping and order is both available before we allow submission
   */
  const isInvoiceSubmissionDisabled = isOrderLoading || shippingLoading;

  /**
   * If there are orders scanned, add a case for each order to the invoiceCases List.
   */
  useEffect(() => {
    if (!ordersScanned.length || isOrderLoading) return; // If there are no orders scanned or invoice is loading, do nothing.
    const filteredOrdersScanned: CreatedOrder[] = ordersScanned.filter((order: CreatedOrder) => {
      const isOrderAlreadyInInvoice = invoiceCases.find(({ orderNumber }) => orderNumber === order.orderNumber);
      return !isOrderAlreadyInInvoice;
    });

    if (!filteredOrdersScanned.length) return; // If there are no orders to add, do nothing.
    const scannedOrderNumberPromises = filteredOrdersScanned.map(({ orderNumber }) => onFetchOrder(orderNumber));

    const fetchMultipleOrders = async () => {
      try {
        const scannedOrders = await Promise.all(scannedOrderNumberPromises);
        scannedOrders.forEach(scannedOrder => {
          if (!scannedOrder) return;
          newCase(scannedOrder);
        });
      } catch (err) {
        const error = err as Error;
        console.error('Error while fetching scanned orders: ', error);
        toast.notify(
          `Error while fetching scanned orders: ${concatOrderNumber(filteredOrdersScanned)}`,
          ToastNotificationType.Error
        );
      }
    };
    fetchMultipleOrders();
  }, [ordersScanned, isOrderLoading, newCase, invoiceCases, onFetchOrder, toast]);

  /**
   *
   * React useEffect hook to automatically refresh product return requirements when the number of invoice cases changes.
   * - Calls 'refreshProductRequirement' if the current invoice cases are greater than previous invoice cases length which means a new case is added.
   *
   */
  useEffect(() => {
    if (prevInvoiceCase?.length && invoiceCases.length && invoiceCases.length > prevInvoiceCase.length) {
      refreshProductReturnRequirements(invoiceCases);
    }
  }, [prevInvoiceCase, invoiceCases, refreshProductReturnRequirements]);

  if (invoiceError) {
    return (
      <ModuleContainer>
        <ModuleHeader title={invoicingHeaderTitle}></ModuleHeader>
        <div className="flex flex-col items-center justify-center h-full">
          <div className="shadow-md rounded-md bg-white py-12 px-6 my-4 w-full flex flex-col gap-4">
            <div className="text-2xl font-semibold text-red-600 text-center">
              Error occurred while loading the case.
            </div>
            <div className="text-md font-semibold text-red-600 text-center">{invoiceError.message}</div>
          </div>
        </div>
      </ModuleContainer>
    );
  }

  return (
    <ModuleContainer>
      <InvoiceHeader
        isLoading={isInvoiceSubmissionDisabled}
        title={invoicingHeaderTitle}
        onCancel={cancelHandler}
        onSubmit={submitHandler}
        onRefresh={onRefreshOrder}
        isOrderProcessing={isOrderProcessing}
      />
      <div className="flex overflow-y-scroll bg-gray-50">
        <InvoiceDetail
          isLoading={isOrderLoading}
          isShippingLoading={shippingLoading}
          onRefresh={onRefreshOrder}
          orderBeingProcessed={processingOrder}
        />
        <RightPanel isLoading={isInvoiceSummaryLoading} productReturnRequirements={productReturnRequirements || []} />
      </div>
    </ModuleContainer>
  );
};

export default InvoicingPage;
